import React from 'react';
import EditDrawer from './EditDrawer';
import Field from '../../Field';
import { Radio } from '../../FormControls';
import { coreSync } from '../../../lib/core';
import s from './editStudent.module.css';
import { DecimalInput } from '../../RegionInputs';

// Drawer 3 of five — the pencil on the Lesson Settings card (#120).
//
// What a lesson costs and how long it runs, per student. As in MMS, every field
// including Rate stays editable once money has moved; a changed rate does not
// rewrite a charge already raised, because the ledger holds its own amounts.
//
// MMS's fifth row, Set Make-Up Credits, is `SetMakeupCreditsDrawer` on the
// same card (#178): credits are a collection of their own (data/useMakeup.jsx),
// so setting one is a write to a different collection on its own transaction,
// and putting it behind this Save would commit two unrelated writes with one
// button. The head of that file has the argument.
//
// The other four rows match MMS: its Lesson Price is four radios with help
// text, and `MODES` in `core/src/pricing.rs` matches them word for word
// (#178).
const RATE_HINT = {
  hourly: 'Per hour',
  fixed_monthly: 'Per month',
};

export default function EditLessonSettingsDrawer({
  open, form, errors, saving, categories = [], onChange, onChooseCategory, onClose, onSave,
}) {
  if (!open) return null;

  return (
    <EditDrawer open title="Lesson Settings" saving={saving} onClose={onClose} onSave={onSave}>
      <div className={s.pair}>
        {/* Keyed on the category's id, as the wizard's and the event form's
            selects are (#110, #128): a label cannot tell two categories of one
            name apart. */}
        <Field
          label="Category" htmlFor="edit-category"
          hint="Manage the list from Calendar → Options"
          error={errors.categoryId}
        >
          <select
            id="edit-category" className={s.input}
            value={form.categoryId}
            onChange={(e) => onChooseCategory(e.target.value)}
          >
            {/* A student with a label and no id (domain/people.js): rather than
                showing the first category as though it were theirs, the box
                says what the document holds, and saving picks one. */}
            {form.categoryId ? null : (
              <option value="">{form.categoryLabel ? `${form.categoryLabel} (not in the list)` : 'Choose…'}</option>
            )}
            {categories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </Field>

        {/* Whole minutes, 1 to 1440, and required, as firestore.rules carries:
            `requiredIntInRange(d, 'defaultDurationMin', 1, 1440)` (#121, #149,
            #352). MMS refuses an emptied Duration on an existing student with
            the same sentence as on a new one. The message is `durationError`'s,
            through `lessonErrors` in hooks/useStudentEdit.js, so the wizard and
            this drawer agree on what a duration is. */}
        <Field label="Duration" htmlFor="edit-duration" hint="Minutes" error={errors.durationMin}>
          <input
            id="edit-duration" type="number" min="5" step="5" className={s.input}
            value={form.durationMin} onChange={(e) => onChange('durationMin', e.target.value)}
          />
        </Field>
      </div>

      <div className={s.groupLabelSpaced}>Lesson Price</div>
      {/* The four modes and their copy come from the core (#214), so the radio
          list and the price it computes agree about what `fixed_monthly` is
          called. */}
      {coreSync().pricingModes().map((m) => (
        <Radio
          key={m.id} name="edit-pmode" label={m.label} help={m.help}
          checked={form.pricingMode === m.id}
          onChange={() => onChange('pricingMode', m.id)}
        />
      ))}

      {form.pricingMode !== 'none' ? (
        <div className={[s.spaced, s.narrow].join(' ')}>
          {/* The ceiling `optionalMinor` carries (#164), refused at the field
              rather than as a permission error on Save. */}
          <Field
            label="Rate" htmlFor="edit-rate"
            hint={RATE_HINT[form.pricingMode] || 'Per lesson'}
            error={errors.rate}
          >
            {/* The box is the Region's (`DecimalInput`, #526): `16,95` in
                Germany. A third decimal is truncated one level up, by `set` in
                src/hooks/useStudentEdit.js. */}
            <DecimalInput
              id="edit-rate" className={s.input}
              value={form.rate} onChange={(e) => onChange('rate', e.target.value)}
            />
          </Field>
        </div>
      ) : null}
    </EditDrawer>
  );
}
